import React from 'react';
import { Clock, Shield, MapPin, CheckCircle, Phone, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEOHelmet from '../components/SEOHelmet';
import InternalLinks from '../components/InternalLinks';
import AdvancedSEO from '../components/AdvancedSEO';
import StaticContentHelper from '../components/StaticContentHelper';
import Breadcrumbs from '../components/Breadcrumbs';

const ProcessServingPage = () => {
  const processServingSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Process Serving Services California",
    "description": "Professional process serving throughout California with same-day options. Registered and bonded process servers for all legal document types.",
    "provider": {
      "@type": "LegalService",
      "name": "Legal Document Specialist",
      "telephone": "(760) 866-9182",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "225 South Civic Drive, Ste 2-16",
        "addressLocality": "Palm Springs",
        "addressRegion": "CA",
        "postalCode": "92262",
        "addressCountry": "US"
      }
    },
    "areaServed": {
      "@type": "State",
      "name": "California"
    },
    "availableChannel": {
      "@type": "ServiceChannel",
      "availableLanguage": "English",
      "servicePhone": "(760) 866-9182",
      "serviceUrl": `${window.location.origin}/process-serving`
    },
    "offers": [
      {
        "@type": "Offer",
        "name": "Routine Process Serving",
        "description": "Standard process serving within 4 business days",
      },
      {
        "@type": "Offer",
        "name": "Same Day Process Serving",
        "description": "Urgent same-day process serving",
      }
    ]
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "How long does process serving take in California?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Routine process serving typically takes 3-5 business days. However, we offer priority service (1-2 days) and same-day service for urgent matters. The timeline depends on the recipient's availability and the service method required."
        }
      },
      {
        "@type": "Question",
        "name": "What happens if the person can't be found?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "If personal service is not possible after multiple attempts, we can use substituted service (leaving documents with a responsible person at home or work plus mailing). In difficult cases, we may recommend skip tracing services to locate the individual."
        }
      },
      {
        "@type": "Question",
        "name": "Do process servers need to be registered in California?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, any person who serves more than 10 documents per year must register with their county clerk, post a $2,000 surety bond, and pass a background check. All our servers meet these requirements."
        }
      },
      {
        "@type": "Question",
        "name": "Can process serving be done on weekends?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, process serving can be performed any day of the week, including weekends and holidays. Weekend service may increase your chances of finding recipients at home."
        }
      },
      {
        "@type": "Question",
        "name": "What is Proof of Service and why is it important?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Proof of Service (form POS-010) is a legal document completed by the server declaring under penalty of perjury that service was completed. It must be filed with the court to prove proper notification and allow the case to proceed."
        }
      },
      {
        "@type": "Question",
        "name": "How much does process serving cost in California?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Costs depend on the service level (Routine, Priority, Same Day, or On-Demand), location, and complexity. Email your case specifics to quote@legaldocspecialist.com for a quote."
        }
      }
    ]
  };

  React.useEffect(() => {
    const serviceScript = document.createElement('script');
    serviceScript.type = 'application/ld+json';
    serviceScript.textContent = JSON.stringify(processServingSchema);
    document.head.appendChild(serviceScript);

    const faqScript = document.createElement('script');
    faqScript.type = 'application/ld+json';
    faqScript.textContent = JSON.stringify(faqSchema);
    document.head.appendChild(faqScript);

    return () => {
      const scripts = document.querySelectorAll('script[type="application/ld+json"]');
      scripts.forEach(script => {
        if (script.textContent === JSON.stringify(processServingSchema) ||
            script.textContent === JSON.stringify(faqSchema)) {
          script.remove();
        }
      });
    };
  }, []);

  const serviceData = [
    {
      name: 'Routine Process Serving',
      description: 'Standard process serving within 4 business days',
    },
    {
      name: 'Priority Process Serving', 
      description: 'Next business day service',
    },
    {
      name: 'Same Day Process Serving',
      description: 'Urgent same-day process serving',
    },
    {
      name: 'On-Demand Process Serving',
      description: 'Emergency service under 2 hours',
    }
  ];

  const cities = [
    'Palm Springs', 'Riverside', 'Temecula', 'Palm Desert', 'Indio', 'Coachella',
    'La Quinta', 'Rancho Mirage', 'Cathedral City', 'Desert Hot Springs',
    'Indian Wells', 'Thousand Palms', 'Twentynine Palms', 'Yucca Valley',
    'Joshua Tree', 'Morongo Valley', 'Landers', 'Moreno Valley', 'Corona',
    'Menifee', 'Murrieta'
  ];

  return (
    <div>
      <AdvancedSEO 
        title="Process Serving California | Same Day Service Available | Legal Document Specialist"
        description="Professional process serving throughout California. Same-day, priority, and routine service options. Registered & bonded process servers. Request a quote online."
        canonical="/process-serving"
        pageType="Service"
        additionalMeta={{
          'service-type': 'Process Serving',
          'geographic-area': 'California',
          'business-hours': 'Monday-Friday 8:00 AM - 6:00 PM PST'
        }}
      />
      <StaticContentHelper 
        pageName="Process Serving Services"
        services={serviceData}
        cities={cities}
      />
      <SEOHelmet 
        title="Process Serving California | Same Day Service Available | Legal Document Specialist"
        description="Professional process serving throughout California. Same-day, priority, and routine service options. Registered & bonded process servers. Request a quote online."
        keywords="process serving California, same day process server, legal document service, court documents, summons service, California process server, registered bonded"
        canonical="/process-serving"
      />
      <Header />
      <Breadcrumbs />

      {/* Hero Section */}
      <section style={{ backgroundColor: 'var(--bg-page)', padding: '160px 0 80px 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', maxWidth: '800px', margin: '0 auto' }}>
            <h1 className="brand-display" style={{
              marginBottom: '1.5rem',
              color: 'var(--text-primary)'
            }}>
              California Process Serving Services
            </h1>
            <p className="body-large" style={{
              color: 'var(--text-secondary)',
              marginBottom: '2rem',
              lineHeight: '1.6'
            }}>
              Professional process serving throughout California with same-day options available. Registered and bonded process servers for all legal document types.
            </p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/quote?service=process-serving" className="btn-primary" data-testid="hero-quote-link">
                Get Process Serving Quote
              </Link>
              <a href="mailto:support@legaldocspecialist.com" className="btn-secondary">
                Email Us
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Service Levels */}
      <section style={{ backgroundColor: 'var(--bg-card)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <h2 className="heading-1" style={{ marginBottom: '1rem' }}>
              Process Serving Service Levels
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
              Choose the service level that meets your timeline and budget requirements
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '2rem'
          }}>
            {[
              {
                name: 'Routine Service',
                timeline: '4 Business Days',
                description: 'Standard process serving for non-urgent matters',
                features: ['4 business day timeline', 'Up to 3 service attempts', 'Detailed proof of service', 'GPS Data']
              },
              {
                name: 'Priority Service',
                timeline: 'Next Business Day',
                description: 'Expedited service for time-sensitive documents',
                features: ['Next business day service', 'Priority scheduling', 'Real-time updates', 'Detailed reporting']
              },
              {
                name: 'Same Day Service',
                timeline: 'Same Day',
                description: 'Urgent same-day process serving',
                features: ['Same day completion', 'Immediate dispatch', 'Hourly updates', 'Emergency service available']
              },
              {
                name: 'On-Demand Service',
                timeline: 'Under 2 Hours',
                description: 'Emergency process serving for critical deadlines',
                features: ['Under 2 hour service', 'Immediate response', 'Live tracking', 'Court deadline specialists']
              }
            ].map((service, index) => (
              <div key={index} style={{
                backgroundColor: 'var(--bg-page)',
                padding: '2rem',
                borderRadius: '12px',
                border: '1px solid var(--border-medium)',
                textAlign: 'center'
              }}>
                <div style={{
                  width: '60px',
                  height: '60px',
                  backgroundColor: 'var(--brand-primary)',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 1.5rem'
                }}>
                  <Clock size={28} color="white" />
                </div>
                
                <h3 className="heading-3" style={{ marginBottom: '0.5rem' }}>
                  {service.name}
                </h3>
                
                <Link to="/quote?service=process-serving" data-testid={`quote-link-${index}`} style={{ display: 'inline-block', fontSize: '0.95rem', fontWeight: '600', color: 'var(--brand-primary)', marginBottom: '0.5rem', textDecoration: 'underline' }}>Request a Quote</Link>
                
                <p style={{
                  color: 'var(--text-secondary)',
                  fontSize: '0.9rem',
                  marginBottom: '1rem'
                }}>
                  {service.timeline}
                </p>
                
                <p className="body-small" style={{
                  color: 'var(--text-secondary)',
                  marginBottom: '1.5rem'
                }}>
                  {service.description}
                </p>
                
                <ul style={{
                  textAlign: 'left',
                  paddingLeft: '0',
                  listStyle: 'none'
                }}>
                  {service.features.map((feature, fIndex) => (
                    <li key={fIndex} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      marginBottom: '0.5rem',
                      fontSize: '0.9rem',
                      color: 'var(--text-secondary)'
                    }}>
                      <CheckCircle size={16} color="var(--brand-primary)" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How Process Serving Works */}
      <section style={{ backgroundColor: 'var(--bg-page)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <h2 className="heading-1" style={{ marginBottom: '1rem' }}>
              How California Process Serving Works
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '800px', margin: '0 auto' }}>
              Understanding the process serving workflow helps ensure your legal documents are delivered properly and on time
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '2rem',
            marginBottom: '4rem'
          }}>
            {[
              {
                step: '1',
                title: 'Document Submission',
                description: 'Submit your legal documents through our secure online platform, email, or in person. Provide recipient details and service address information.'
              },
              {
                step: '2',
                title: 'Server Assignment',
                description: 'We assign a registered and bonded process server in the recipient\'s area. All our servers are background-checked and fully compliant with California Code of Civil Procedure.'
              },
              {
                step: '3',
                title: 'Service Attempts',
                description: 'Our server makes up to 3 professional service attempts at different times and days. We use GPS tracking and detailed logs for every attempt.'
              },
              {
                step: '4',
                title: 'Proof of Service',
                description: 'Upon successful service, you receive a completed Proof of Service (form POS-010) with declaration under penalty of perjury, ready for court filing.'
              }
            ].map((item, index) => (
              <div key={index} style={{
                backgroundColor: 'var(--bg-card)',
                padding: '2rem',
                borderRadius: '12px',
                border: '1px solid var(--border-medium)',
                position: 'relative'
              }}>
                <div style={{
                  position: 'absolute',
                  top: '-20px',
                  left: '20px',
                  width: '40px',
                  height: '40px',
                  backgroundColor: 'var(--brand-primary)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontWeight: '700',
                  fontSize: '1.2rem'
                }}>
                  {item.step}
                </div>
                <h3 className="heading-3" style={{ marginTop: '1rem', marginBottom: '1rem' }}>
                  {item.title}
                </h3>
                <p style={{ color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                  {item.description}
                </p>
              </div>
            ))}
          </div>

          {/* Process Serving Requirements */}
          <div style={{
            backgroundColor: 'var(--bg-card)',
            padding: '3rem',
            borderRadius: '12px',
            border: '2px solid var(--border-medium)',
            maxWidth: '900px',
            margin: '0 auto'
          }}>
            <h3 className="heading-2" style={{ marginBottom: '2rem', textAlign: 'center' }}>
              California Process Serving Legal Requirements
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '2rem' }}>
              <div>
                <h4 className="heading-4" style={{ color: 'var(--brand-primary)', marginBottom: '1rem' }}>
                  Server Qualifications
                </h4>
                <ul style={{ paddingLeft: '1.5rem', color: 'var(--text-secondary)', lineHeight: '1.8' }}>
                  <li>18 years or older</li>
                  <li>Not a party to the case</li>
                  <li>Registered with county clerk</li>
                  <li>$2,000 surety bond</li>
                  <li>Background checked</li>
                </ul>
              </div>
              <div>
                <h4 className="heading-4" style={{ color: 'var(--brand-primary)', marginBottom: '1rem' }}>
                  Service Methods
                </h4>
                <ul style={{ paddingLeft: '1.5rem', color: 'var(--text-secondary)', lineHeight: '1.8' }}>
                  <li>Personal service (preferred)</li>
                  <li>Substituted service</li>
                  <li>Service on authorized agent</li>
                  <li>Service by mail (limited cases)</li>
                  <li>Service by publication (court order)</li>
                </ul>
              </div>
              <div>
                <h4 className="heading-4" style={{ color: 'var(--brand-primary)', marginBottom: '1rem' }}>
                  Timing Requirements
                </h4>
                <ul style={{ paddingLeft: '1.5rem', color: 'var(--text-secondary)', lineHeight: '1.8' }}>
                  <li>Serve within 60 days of filing</li>
                  <li>Minimum 5 days before hearing (most cases)</li>
                  <li>15 days for summons & complaint</li>
                  <li>Specific timelines for family law</li>
                  <li>Court deadline compliance</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section style={{ backgroundColor: 'var(--bg-card)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <h2 className="heading-1" style={{ marginBottom: '1rem' }}>
              Why Choose Legal Document Specialist
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '700px', margin: '0 auto' }}>
              With over 22 years of experience serving California law firms, we deliver reliability, speed, and professionalism on every assignment
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '2rem'
          }}>
            {[
              {
                icon: <Shield size={28} color="white" />,
                title: 'Registered & Bonded',
                description: 'All our process servers are registered with California county clerks, carry $2,000 surety bonds, and pass comprehensive background checks as required by state law.'
              },
              {
                icon: <MapPin size={28} color="white" />,
                title: 'Statewide Coverage',
                description: 'Serving all 58 California counties with our network of professional servers. From Los Angeles to San Francisco, San Diego to Sacramento—we have local coverage everywhere.'
              },
              {
                icon: <Clock size={28} color="white" />,
                title: 'Fast Turnaround',
                description: 'Same-day service available in most areas. Priority and rush services ensure you never miss a filing deadline. Average routine service completed in 2-3 days.'
              },
              {
                icon: <CheckCircle size={28} color="white" />,
                title: '95% First-Attempt Success',
                description: 'Our intelligent scheduling and skilled servers achieve successful service on the first attempt in 95% of cases, saving you time and reducing costs.'
              },
              {
                icon: <Phone size={28} color="white" />,
                title: 'Real-Time Updates',
                description: 'Track your service in real-time through our platform. Receive instant notifications when service is completed with GPS-verified timestamps and photo documentation.'
              },
              {
                icon: <Mail size={28} color="white" />,
                title: 'Court-Ready Documents',
                description: 'Receive properly completed Proof of Service forms (POS-010) with declarations under penalty of perjury, ready for immediate court filing.'
              }
            ].map((item, index) => (
              <div key={index} style={{
                backgroundColor: 'var(--bg-page)',
                padding: '2rem',
                borderRadius: '12px',
                border: '1px solid var(--border-medium)',
                textAlign: 'center'
              }}>
                <div style={{
                  width: '60px',
                  height: '60px',
                  backgroundColor: 'var(--brand-primary)',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 1.5rem'
                }}>
                  {item.icon}
                </div>
                <h3 className="heading-3" style={{ marginBottom: '1rem' }}>
                  {item.title}
                </h3>
                <p style={{ color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Document Types */}
      <section style={{ backgroundColor: 'var(--bg-page)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <h2 className="heading-1" style={{ marginBottom: '1rem' }}>
              Legal Documents We Serve
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
              Professional service for all types of legal documents throughout California
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '1.5rem'
          }}>
            {[
              'Summons & Complaints', 'Subpoenas', 'Divorce Papers', 'Restraining Orders',
              'Eviction Notices', 'Small Claims Documents', 'Family Law Documents',
              'Business Litigation', 'Personal Injury Cases', 'Employment Law Documents',
              'Probate Documents', 'Real Estate Legal Papers'
            ].map((docType, index) => (
              <div key={index} style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                padding: '1rem',
                backgroundColor: 'var(--bg-card)',
                borderRadius: '8px',
                border: '1px solid var(--border-light)'
              }}>
                <Shield size={20} color="var(--brand-primary)" />
                <span style={{ color: 'var(--text-primary)', fontWeight: '500' }}>
                  {docType}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Learn More Section */}
      <section style={{ backgroundColor: 'var(--bg-page)', padding: '60px 0' }}>
        <div className="container">
          <div style={{
            maxWidth: '800px',
            margin: '0 auto',
            backgroundColor: 'var(--bg-card)',
            padding: '3rem',
            borderRadius: '12px',
            border: '2px solid var(--brand-primary)',
            textAlign: 'center'
          }}>
            <h3 className="heading-2" style={{ marginBottom: '1rem' }}>
              Want to Learn More About Process Serving?
            </h3>
            <p className="body-medium" style={{ color: 'var(--text-secondary)', marginBottom: '2rem', lineHeight: '1.8' }}>
              Read our comprehensive guide covering California process serving requirements, legal procedures, deadlines, and best practices for 2025.
            </p>
            <Link 
              to="/resources/process-serving-guide"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '1rem 2rem',
                backgroundColor: 'var(--brand-primary)',
                color: 'white',
                borderRadius: '6px',
                textDecoration: 'none',
                fontWeight: '600'
              }}
            >
              Read Complete Process Serving Guide →
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section style={{ backgroundColor: 'var(--bg-card)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <h2 className="heading-1" style={{ marginBottom: '1rem' }}>
              Frequently Asked Questions
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
              Common questions about process serving in California
            </p>
          </div>

          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            {[
              {
                question: 'How long does process serving take in California?',
                answer: 'Routine process serving typically takes 3-5 business days. However, we offer priority service (1-2 days) and same-day service for urgent matters. The timeline depends on the recipient\'s availability and the service method required.'
              },
              {
                question: 'What happens if the person can\'t be found?',
                answer: 'If personal service is not possible after multiple attempts, we can use substituted service (leaving documents with a responsible person at home or work plus mailing). In difficult cases, we may recommend skip tracing services to locate the individual.'
              },
              {
                question: 'Do process servers need to be registered in California?',
                answer: 'Yes, any person who serves more than 10 documents per year must register with their county clerk, post a $2,000 surety bond, and pass a background check. All our servers meet these requirements.'
              },
              {
                question: 'Can process serving be done on weekends?',
                answer: 'Yes, process serving can be performed any day of the week, including weekends and holidays. Weekend service may increase your chances of finding recipients at home.'
              },
              {
                question: 'What is Proof of Service and why is it important?',
                answer: 'Proof of Service (form POS-010) is a legal document completed by the server declaring under penalty of perjury that service was completed. It must be filed with the court to prove proper notification and allow the case to proceed.'
              },
              {
                question: 'How much does process serving cost in California?',
                answer: 'Costs depend on the service level (Routine, Priority, Same Day, or On-Demand), location, and complexity. Email your case specifics to quote@legaldocspecialist.com for a quote.'
              }
            ].map((faq, index) => (
              <div key={index} style={{
                backgroundColor: 'var(--bg-page)',
                padding: '2rem',
                borderRadius: '8px',
                marginBottom: '1.5rem',
                border: '1px solid var(--border-medium)'
              }}>
                <h3 className="heading-4" style={{ marginBottom: '1rem', color: 'var(--brand-primary)' }}>
                  {faq.question}
                </h3>
                <p style={{ color: 'var(--text-secondary)', lineHeight: '1.7' }}>
                  {faq.answer}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section style={{
        backgroundColor: 'var(--brand-primary)',
        padding: '80px 0',
        color: 'white'
      }}>
        <div className="container" style={{ textAlign: 'center' }}>
          <h2 className="heading-1" style={{ marginBottom: '1rem', color: 'white' }}>
            Need Process Serving Services?
          </h2>
          <p className="body-large" style={{ marginBottom: '2rem', opacity: 0.9 }}>
            Get professional process serving with same-day options available throughout California
          </p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link 
              to="/place-order"
              style={{
                padding: '1rem 2rem',
                backgroundColor: 'white',
                color: 'var(--brand-primary)',
                borderRadius: '6px',
                textDecoration: 'none',
                fontWeight: '600'
              }}
            >
              Place Order Now
            </Link>
            <a href="/quote"
              style={{
                padding: '1rem 2rem',
                backgroundColor: 'transparent',
                color: 'white',
                border: '2px solid white',
                borderRadius: '6px',
                textDecoration: 'none',
                fontWeight: '600'
              }}
            >
              Request a Quote
            </a>
          </div>
        </div>
      </section>

      {/* Internal Links */}
      <InternalLinks currentPage="/process-serving" />

      <Footer />
    </div>
  );
};

export default ProcessServingPage;